import { useMutation, useQuery } from "@apollo/client";
import {
	Anchor,
	Button,
	Card,
	Container,
	Group,
	Loader,
	Modal,
	Pagination,
	ScrollArea,
	Stack,
	Table,
	Text,
	TextInput,
	Title,
	Tooltip,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { IconBuilding, IconSearch, IconTrash } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { EmployeeGetOrganizationsQuery } from "../../../graphql/generated/types.gen";
import {
	EMPLOYEE_DELETE_ORGANIZATION,
	EMPLOYEE_GET_ORGANIZATIONS,
} from "../../../graphql/queries";
import { useAuthCompat } from "../../../hooks/useAuthCompat";
import { useIsMobile } from "../../../hooks/useIsMobile";

export const Route = createFileRoute("/admin/organizations/")({
	component: AdminOrganizationsPage,
});

const PAGE_SIZE = 20;

function AdminOrganizationsPage() {
	const { user, isLoading: authLoading } = useAuthCompat();
	const isMobile = useIsMobile();

	const [search, setSearch] = useState("");
	const [debouncedSearch] = useDebouncedValue(search, 300);
	const [page, setPage] = useState(1);

	const [
		deleteModalOpened,
		{ open: openDeleteModal, close: closeDeleteModal },
	] = useDisclosure(false);
	const [orgToDelete, setOrgToDelete] = useState<{
		id: string;
		name: string;
	} | null>(null);

	useEffect(() => {
		if (authLoading) return;
		if (!user) {
			window.location.href = "/welcome";
			return;
		}
		if (!user.isEmployee) {
			window.location.href = "/";
		}
	}, [user, authLoading]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Reset page when search changes
	useEffect(() => {
		setPage(1);
	}, [debouncedSearch]);

	const { data, loading, refetch } = useQuery<EmployeeGetOrganizationsQuery>(
		EMPLOYEE_GET_ORGANIZATIONS,
		{
			variables: {
				search: debouncedSearch || undefined,
				limit: PAGE_SIZE,
				offset: (page - 1) * PAGE_SIZE,
			},
			skip: !user?.isEmployee,
		},
	);

	const [deleteOrg, { loading: deleting }] = useMutation(
		EMPLOYEE_DELETE_ORGANIZATION,
		{
			onCompleted: () => {
				closeDeleteModal();
				setOrgToDelete(null);
				// If this was the last item on the page, go back one page
				if (organizations.length === 1 && page > 1) {
					setPage(page - 1);
				} else {
					refetch();
				}
			},
			onError: (error) => {
				alert(`Failed to delete organization: ${error.message}`);
			},
		},
	);

	const handleDeleteClick = (org: { id: string; name: string }) => {
		setOrgToDelete(org);
		openDeleteModal();
	};

	const confirmDelete = () => {
		if (orgToDelete) {
			deleteOrg({ variables: { organizationId: orgToDelete.id } });
		}
	};

	if (authLoading || !user?.isEmployee) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader size="lg" />
			</div>
		);
	}

	const organizations = data?.employeeOrganizations.organizations || [];
	const totalCount = data?.employeeOrganizations.totalCount || 0;
	const totalPages = Math.ceil(totalCount / PAGE_SIZE);

	return (
		<div className="h-[calc(100vh-var(--header-height))] overflow-auto bg-[var(--mantine-color-body)]">
			<Container size="lg" py="xl" px={isMobile ? "md" : "xl"}>
				<Group gap="md" mb="xs" wrap="nowrap">
					<IconBuilding
						size={isMobile ? 24 : 32}
						color="var(--mantine-color-blue-6)"
					/>
					<Title order={isMobile ? 2 : 1}>Organizations</Title>
				</Group>
				<div className="mb-6">
					<Anchor href="/admin">&larr; Back to Admin</Anchor>
				</div>

				<TextInput
					placeholder="Search by name..."
					leftSection={<IconSearch size={16} />}
					value={search}
					onChange={(e) => setSearch(e.currentTarget.value)}
					mb="md"
				/>

				{loading ? (
					<div className="flex items-center justify-center py-8">
						<Loader size="lg" />
					</div>
				) : organizations.length === 0 ? (
					<Card shadow="sm" padding="xl" radius="md" withBorder>
						<Text size="lg" c="dimmed" ta="center">
							{debouncedSearch
								? "No organizations found."
								: "No organizations yet."}
						</Text>
					</Card>
				) : isMobile ? (
					<Stack gap="md">
						{organizations.map((org) => (
							<Card
								key={org.id}
								shadow="sm"
								padding="md"
								radius="md"
								withBorder
							>
								<Stack gap="xs">
									<Link
										to="/admin/organizations/$organizationId"
										params={{ organizationId: org.id }}
										style={{ textDecoration: "none", color: "inherit" }}
									>
										<Text fw={600}>{org.name}</Text>
									</Link>
									{org.websiteUrl && (
										<Text
											size="sm"
											c="dimmed"
											style={{ wordBreak: "break-all" }}
										>
											{org.websiteUrl}
										</Text>
									)}
									<Group justify="space-between" align="center">
										<Text size="sm" c="dimmed">
											{org.members.length} member
											{org.members.length !== 1 && "s"}
										</Text>
										<Tooltip label={new Date(org.createdAt).toLocaleString()}>
											<Text size="xs" c="dimmed" style={{ cursor: "default" }}>
												{new Date(org.createdAt).toLocaleDateString()}
											</Text>
										</Tooltip>
									</Group>
									<Group justify="flex-end">
										<Button
											size="xs"
											color="red"
											variant="light"
											leftSection={<IconTrash size={14} />}
											onClick={() => handleDeleteClick(org)}
										>
											Delete
										</Button>
									</Group>
								</Stack>
							</Card>
						))}
					</Stack>
				) : (
					<Card shadow="sm" padding={0} radius="md" withBorder>
						<ScrollArea>
							<Table striped highlightOnHover>
								<Table.Thead>
									<Table.Tr>
										<Table.Th>Name</Table.Th>
										<Table.Th>Website</Table.Th>
										<Table.Th>Members</Table.Th>
										<Table.Th>Created</Table.Th>
										<Table.Th>Actions</Table.Th>
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>
									{organizations.map((org) => (
										<Table.Tr key={org.id}>
											<Table.Td>
												<Link
													to="/admin/organizations/$organizationId"
													params={{ organizationId: org.id }}
													style={{ textDecoration: "none" }}
												>
													<Group gap="xs" wrap="nowrap">
														<IconBuilding size={16} />
														<Text size="sm">{org.name}</Text>
													</Group>
												</Link>
											</Table.Td>
											<Table.Td>
												{org.websiteUrl ? (
													<Text
														size="sm"
														c="dimmed"
														style={{
															maxWidth: 200,
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
														}}
													>
														{org.websiteUrl}
													</Text>
												) : (
													<Text size="sm" c="dimmed">
														—
													</Text>
												)}
											</Table.Td>
											<Table.Td>{org.members.length}</Table.Td>
											<Table.Td>
												<Tooltip
													label={new Date(org.createdAt).toLocaleString()}
												>
													<Text size="sm" style={{ cursor: "default" }}>
														{new Date(org.createdAt).toLocaleDateString()}
													</Text>
												</Tooltip>
											</Table.Td>
											<Table.Td>
												<Button
													size="xs"
													color="red"
													variant="light"
													leftSection={<IconTrash size={14} />}
													onClick={() => handleDeleteClick(org)}
												>
													Delete
												</Button>
											</Table.Td>
										</Table.Tr>
									))}
								</Table.Tbody>
							</Table>
						</ScrollArea>
					</Card>
				)}

				{totalPages > 1 && (
					<Group justify="center" mt="md">
						<Pagination
							value={page}
							onChange={setPage}
							total={totalPages}
							size={isMobile ? "sm" : "md"}
						/>
						<Text size="sm" c="dimmed">
							{totalCount} organization{totalCount !== 1 && "s"} total
						</Text>
					</Group>
				)}
			</Container>

			<Modal
				opened={deleteModalOpened}
				onClose={closeDeleteModal}
				title="Delete Organization"
				centered
			>
				<Stack>
					<Text>
						Are you sure you want to delete <strong>{orgToDelete?.name}</strong>
						?
					</Text>
					<Text size="sm" c="dimmed">
						This will remove the organization and all associated data including
						nodes, edges, and member associations. This action cannot be undone.
					</Text>
					<Group justify="flex-end" mt="md">
						<Button variant="default" onClick={closeDeleteModal}>
							Cancel
						</Button>
						<Button color="red" onClick={confirmDelete} loading={deleting}>
							Delete
						</Button>
					</Group>
				</Stack>
			</Modal>
		</div>
	);
}
